import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { extractPermissionsFromToken } from '../auth/auth-permissions.util';
import { extractBackofficeRoleFromToken } from '../auth/role-access.util';
import { AuthSessionState } from '../models/auth.model';

const AUTH_STORAGE_KEYS = [
  'auth_token',
  'auth_refreshToken',
  'auth_userName',
  'auth_role',
  'auth_publicId',
  'auth_permissions',
  'auth_userType',
  'auth_roles',
];
const AUTH_LOGOUT_EVENT_KEY = 'auth_logout_event';
const AUTH_CHANNEL_NAME = 'pise-auth-session';

export interface SaveSessionParams {
  token: string;
  refreshToken?: string | null;
  userName: string;
  role: string;
  publicId: string;
  permissions?: string[];
  userType?: string | null;
  roles?: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthSessionSyncService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private channel: BroadcastChannel | null = null;
  private started = false;

  start(): void {
    if (this.started || !this.hasBrowserStorage()) {
      return;
    }

    this.started = true;
    window.addEventListener('storage', this.handleStorageEvent);

    if ('BroadcastChannel' in window) {
      this.channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
      this.channel.onmessage = (event: MessageEvent<{ type?: string }>) => {
        if (event.data?.type === 'logout') {
          this.zone.run(() => this.clearAndRedirectToLogin());
        }
      };
    }
  }

  saveSession(params: SaveSessionParams): void {
    if (!this.hasBrowserStorage()) {
      return;
    }

    const {
      token,
      refreshToken = null,
      userName,
      role,
      publicId,
      permissions = [],
      userType = null,
      roles = [],
    } = params;

    this.clearStoredSession();
    sessionStorage.setItem('auth_token', token);
    if (refreshToken) {
      sessionStorage.setItem('auth_refreshToken', refreshToken);
    }
    sessionStorage.setItem('auth_userName', userName);
    sessionStorage.setItem('auth_role', role);
    sessionStorage.setItem('auth_publicId', publicId);
    sessionStorage.setItem('auth_permissions', JSON.stringify(permissions));
    sessionStorage.setItem('auth_roles', JSON.stringify(roles));

    if (userType !== null) {
      sessionStorage.setItem('auth_userType', userType);
    }
  }

  updateTokens(token: string, refreshToken: string): void {
    if (!this.hasBrowserStorage()) {
      return;
    }

    sessionStorage.setItem('auth_token', token);
    sessionStorage.setItem('auth_refreshToken', refreshToken);
  }

  getSessionValue(key: string): string | null {
    if (!this.hasBrowserStorage()) {
      return null;
    }

    return sessionStorage.getItem(key);
  }

  readSession(): AuthSessionState {
    const token = this.getSessionValue('auth_token');
    const storedPermissions = this.getSessionValue('auth_permissions');
    const storedRoles = this.getSessionValue('auth_roles');

    return {
      token,
      refreshToken: this.getSessionValue('auth_refreshToken'),
      userName: this.getSessionValue('auth_userName'),
      role: this.getSessionValue('auth_role') ?? extractBackofficeRoleFromToken(token),
      publicId: this.getSessionValue('auth_publicId'),
      permissions: this.getStoredPermissions(token, storedPermissions),
      userType: this.getSessionValue('auth_userType'),
      roles: this.getStoredRoles(storedRoles),
    };
  }

  broadcastLogout(): void {
    if (!this.hasBrowserStorage()) {
      return;
    }

    this.clearStoredSession();
    localStorage.setItem(AUTH_LOGOUT_EVENT_KEY, String(Date.now()));
    this.channel?.postMessage({ type: 'logout' });
  }

  clearStoredSession(): void {
    if (!this.hasBrowserStorage()) {
      return;
    }

    AUTH_STORAGE_KEYS.forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  }

  clearAndRedirectToLogin(): void {
    this.clearStoredSession();

    if (!this.router.url.includes('/login')) {
      this.router.navigate(['/login']);
    }
  }

  private readonly handleStorageEvent = (event: StorageEvent): void => {
    if (event.key === AUTH_LOGOUT_EVENT_KEY) {
      this.zone.run(() => this.clearAndRedirectToLogin());
    }
  };

  private hasBrowserStorage(): boolean {
    return typeof window !== 'undefined' && !!window.sessionStorage && !!window.localStorage;
  }

  private getStoredPermissions(token: string | null, storedPermissions: string | null): string[] {
    if (!storedPermissions) {
      return extractPermissionsFromToken(token);
    }

    try {
      const parsed = JSON.parse(storedPermissions);
      return Array.isArray(parsed) ? parsed : extractPermissionsFromToken(token);
    } catch {
      return extractPermissionsFromToken(token);
    }
  }

  private getStoredRoles(storedRoles: string | null): string[] {
    if (!storedRoles) {
      return [];
    }

    try {
      const parsed = JSON.parse(storedRoles);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
